<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxAlert, EcxButton } from '@ecx/ui'
import { DocumentDownloadIcon } from '@ecx/ui/icons'
import { useBulkImportStore } from '@/stores/bulkImport'
import type { BulkImportEntity } from '@/types/bulkImport'
import { useActorSegmentation } from '@/composables/segmentation/useActorSegmentation'
import BulkImportFileUpload from '../shared/BulkImportFileUpload.vue'
import BulkImportInstructionsBox from '../shared/BulkImportInstructionsBox.vue'

const DIMENSIONS_WITHOUT_COLUMN = ['cost_center', 'project']

const SCOPE_WARNING_BY_ENTITY: Partial<Record<BulkImportEntity, string>> = {
  vehicles: 'vehicle_import_warning',
  users: 'user_import_warning',
}

const CARD_PRODUCT_BY_ENTITY: Partial<Record<BulkImportEntity, string>> = {
  vehicles: 'fleet',
  users: 'corporate',
}

const PRODUCT_WARNING_BY_ENTITY: Partial<Record<BulkImportEntity, string>> = {
  vehicles: 'vehicle_import_product_warning',
  users: 'user_import_product_warning',
}

const { t } = useI18n()
const store = useBulkImportStore()
const { dimension, values } = useActorSegmentation()
const isEdit = computed(() => store.mode === 'edit')

const scopeWarningKey = computed(() => {
  if (isEdit.value || dimension.value === null) return null

  if (dimension.value === 'product') {
    const produto = CARD_PRODUCT_BY_ENTITY[store.entity]
    if (produto === undefined || values.value.includes(produto)) return null

    return PRODUCT_WARNING_BY_ENTITY[store.entity] ?? null
  }

  if (!DIMENSIONS_WITHOUT_COLUMN.includes(dimension.value)) return null

  return SCOPE_WARNING_BY_ENTITY[store.entity] ?? null
})
</script>

<template>
  <div class="flex flex-col pb-6">
    <div class="px-6 pt-6 pb-0 flex items-center justify-between">
      <span class="text-base text-grey-950">
        {{ isEdit ? t('corp.bulk_import.edit.upload.header') : t('corp.bulk_import.upload.use_model_upload') }}
      </span>
      <EcxButton v-if="!isEdit" variant="outline" size="sm" class="shrink-0 whitespace-nowrap" @click="store.downloadTemplate()">
        <template #iconLeft>
          <DocumentDownloadIcon class="w-5 h-5 text-royal-500" />
        </template>
        {{ t('corp.bulk_import.step_selector.download_template') }}
      </EcxButton>
    </div>

    <EcxAlert
      v-if="scopeWarningKey"
      data-testid="import-scope-warning"
      class="mx-6 mt-4"
      variant="warning"
      :title="t(`corp.segmentation.${scopeWarningKey}.title`)"
      :message="t(`corp.segmentation.${scopeWarningKey}.description`)"
    />

    <div class="mx-6 mt-4">
      <BulkImportFileUpload
        :file="store.file"
        :uploading="store.isUploading"
        :server-error="store.uploadErrorReason"
        @file-selected="store.setFile"
        @replace="store.replaceFile"
      />
    </div>

    <BulkImportInstructionsBox />
  </div>
</template>
